import {
  Box,
  Button,
  Flex,
  Image,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Text,
  VStack,
} from "@chakra-ui/react";
import React, { useCallback, useRef } from "react";
import { MdArrowBack, MdDownload, MdPrint } from "react-icons/md";
import { studentInk, studentPalette } from "../../constants/studentPalette";
import { studentSurfaces } from "../../constants/studentThemeSurfaces";

const FONT_STACK = `'Tajawal','Amiri','Scheherazade New','Noto Sans Arabic','Cairo',sans-serif`;
const IMAGE_BASE = "https://edutopiaonline.com/api";

const C = {
  primary: studentPalette.primary,
  accent: studentPalette.secondary,
  pageBg: studentSurfaces.muted,
  border: studentSurfaces.border,
  cream: studentSurfaces.raised,
  ink: studentInk,
  muted: studentSurfaces.textMuted,
};

function isUsable(value) {
  return Boolean(value) && value !== "null" && value !== "undefined";
}

function asPath(value) {
  if (!isUsable(value)) return "";
  if (typeof value === "object") {
    return asPath(value.url || value.path || value.image || value.filename || value.src);
  }
  return String(value).trim();
}

function isPdfPath(value) {
  const path = asPath(value).toLowerCase();
  return path.endsWith(".pdf") || path.includes(".pdf?");
}

export function questionImageUrl(path) {
  const raw = asPath(path);
  if (!raw || isPdfPath(raw)) return "";
  if (/^https?:\/\//i.test(raw) || raw.startsWith("data:") || raw.startsWith("blob:")) return raw;
  return `${IMAGE_BASE}/${raw.replace(/^\//, "")}`;
}

function questionImages(question) {
  const urls = [];
  const push = (value) => {
    const url = questionImageUrl(value);
    if (url && !urls.includes(url)) urls.push(url);
  };
  push(question?.image);
  push(question?.question_image);
  if (typeof question?.question === "string") {
    const re = /<img\b[^>]*\bsrc\s*=\s*(["']?)([^"'>\s]+)\1/gi;
    let match = re.exec(question.question);
    while (match) {
      push(match[2]);
      match = re.exec(question.question);
    }
  }
  return urls;
}

function stripHtml(value) {
  if (!isUsable(value) || typeof value === "object") return "";
  if (typeof document === "undefined") return String(value).replace(/<[^>]+>/g, " ").trim();
  const host = document.createElement("div");
  host.innerHTML = String(value);
  return (host.textContent || "").replace(/\s+/g, " ").trim();
}

function answerText(answer, index) {
  const letter = String.fromCharCode(65 + index);
  const text = stripHtml(answer?.answer) || stripHtml(answer?.text) || "";
  return text ? `${letter}. ${text}` : `${letter}.`;
}

const TagExamPaperModal = ({
  isOpen,
  onClose,
  title = "Practice exam",
  questions = [],
  backLabel = "Back to exam",
}) => {
  const paperRef = useRef(null);
  const list = Array.isArray(questions) ? questions.filter(Boolean) : [];

  const printPaper = useCallback(() => {
    const node = paperRef.current;
    if (!node) return;
    const popup = window.open("", "_blank", "noopener,noreferrer,width=900,height=1200");
    if (!popup) return;
    popup.document.open();
    popup.document.write(`<!doctype html>
<html>
  <head>
    <title>${String(title || "Practice exam").replace(/</g, "")}</title>
    <style>
      body { font-family: ${FONT_STACK}; color: #2D2A26; margin: 24px; }
      h1 { color: #5B0D16; font-size: 22px; margin: 0 0 8px; }
      .meta { color: #718096; margin-bottom: 24px; }
      .q { page-break-inside: avoid; margin: 0 0 28px; padding-bottom: 16px; border-bottom: 1px solid #E2DDD8; }
      .q h2 { color: #5B0D16; font-size: 14px; letter-spacing: .08em; text-transform: uppercase; margin: 0 0 8px; }
      img { max-width: 100%; height: auto; display: block; margin: 12px 0; }
      .opt { margin: 4px 0; }
      @media print { body { margin: 12mm; } .q { break-inside: avoid; } }
    </style>
  </head>
  <body>${node.innerHTML}</body>
</html>`);
    popup.document.close();
    const runPrint = () => {
      try {
        popup.focus();
        popup.print();
      } catch {
        /* ignore */
      }
    };
    const imgs = Array.from(popup.document.images || []);
    if (!imgs.length) {
      setTimeout(runPrint, 250);
      return;
    }
    let left = imgs.length;
    const done = () => {
      left -= 1;
      if (left <= 0) setTimeout(runPrint, 200);
    };
    imgs.forEach((img) => {
      if (img.complete) done();
      else {
        img.onload = done;
        img.onerror = done;
      }
    });
    setTimeout(runPrint, 4000);
  }, [title]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="full" motionPreset="none" scrollBehavior="inside">
      <ModalOverlay bg="rgba(91, 13, 22, 0.55)" backdropFilter="blur(8px)" />
      <ModalContent
        display="flex"
        flexDirection="column"
        fontFamily={FONT_STACK}
        color={C.ink}
        bg={C.cream}
        m={{ base: 0, md: 6 }}
        borderRadius={{ base: 0, md: "16px" }}
        borderWidth={{ base: 0, md: "1px" }}
        borderColor={C.border}
        maxW={{ base: "100%", md: "1152px" }}
        h={{ base: "100vh", md: "90vh" }}
        maxH={{ base: "100vh", md: "90vh" }}
        overflow="hidden"
        boxShadow={{ md: "0 4px 20px rgba(0, 0, 0, 0.08)" }}
      >
        <Flex
          as="header"
          align="center"
          justify="space-between"
          flexWrap="wrap"
          gap={3}
          px={{ base: 4, md: 6 }}
          py={4}
          bg={C.pageBg}
          borderBottomWidth="1px"
          borderColor={C.border}
        >
          <Button
            type="button"
            variant="ghost"
            leftIcon={<MdArrowBack size={20} />}
            color={C.primary}
            fontWeight="800"
            fontSize="11px"
            letterSpacing="0.14em"
            textTransform="uppercase"
            onClick={onClose}
            _hover={{ bg: "rgba(91, 13, 22, 0.08)" }}
          >
            {backLabel}
          </Button>
          <Flex align="center" gap={{ base: 2, md: 4 }} flexWrap="wrap">
            <Button
              type="button"
              leftIcon={<MdDownload size={18} />}
              bg={C.primary}
              color="white"
              fontWeight="800"
              fontSize="10px"
              letterSpacing="0.14em"
              textTransform="uppercase"
              borderRadius="md"
              px={5}
              py={2}
              h="auto"
              _hover={{ bg: C.accent }}
              onClick={printPaper}
            >
              Download
            </Button>
            <Button
              type="button"
              leftIcon={<MdPrint size={18} />}
              variant="ghost"
              color={C.primary}
              fontWeight="800"
              fontSize="10px"
              letterSpacing="0.14em"
              textTransform="uppercase"
              onClick={printPaper}
              _hover={{ bg: "rgba(91, 13, 22, 0.08)" }}
            >
              Print
            </Button>
          </Flex>
        </Flex>

        <ModalBody p={0} flex="1" minH={0} overflow="auto" bg={C.pageBg}>
          <Box px={{ base: 3, md: 8 }} py={{ base: 3, md: 6 }}>
            <Box
              ref={paperRef}
              maxW="768px"
              mx="auto"
              bg={studentSurfaces.raised}
              borderWidth="1px"
              borderColor={C.border}
              borderRadius="md"
              boxShadow="sm"
              p={{ base: 5, md: 8 }}
            >
              <Text fontWeight="800" color={C.primary} fontSize="xl" mb={1}>
                {title || "Practice exam"}
              </Text>
              <Text className="meta" fontSize="sm" color={C.muted} mb={8}>
                {list.length} question{list.length === 1 ? "" : "s"}
              </Text>

              <VStack align="stretch" spacing={8}>
                {list.map((question, index) => {
                  const images = questionImages(question);
                  const stem = question?.question;
                  const comprehension = stripHtml(question?.comprehension);
                  const answers = Array.isArray(question?.answers) ? question.answers : [];
                  return (
                    <Box key={question?.id || index} className="q">
                      <Text
                        as="h2"
                        fontSize="xs"
                        fontWeight="800"
                        color={C.primary}
                        letterSpacing="0.12em"
                        textTransform="uppercase"
                        mb={2}
                      >
                        Question {index + 1} of {list.length}
                      </Text>
                      {comprehension ? (
                        <Text fontSize="sm" color={C.ink} mb={3} whiteSpace="pre-wrap">
                          {comprehension}
                        </Text>
                      ) : null}
                      {stem ? (
                        <Box
                          fontSize="md"
                          color={C.ink}
                          lineHeight="1.65"
                          sx={{ "& img": { maxW: "100%", h: "auto", my: 3 } }}
                          dangerouslySetInnerHTML={{ __html: stem }}
                        />
                      ) : null}
                      {images.map((src) => (
                        <Image
                          key={src}
                          src={src}
                          alt={`Question ${index + 1}`}
                          maxH="420px"
                          objectFit="contain"
                          my={3}
                        />
                      ))}
                      {answers.length ? (
                        <VStack align="stretch" spacing={1} mt={3}>
                          {answers.map((answer, answerIdx) => (
                            <Text key={answer?.id || answerIdx} className="opt" fontSize="sm">
                              {answerText(answer, answerIdx)}
                            </Text>
                          ))}
                        </VStack>
                      ) : null}
                    </Box>
                  );
                })}
              </VStack>
            </Box>
          </Box>
        </ModalBody>

        <ModalFooter
          justifyContent="center"
          py={4}
          px={{ base: 4, md: 6 }}
          bg={C.pageBg}
          borderTopWidth="1px"
          borderColor={C.border}
        >
          <Text fontSize="xs" fontWeight="700" color={C.muted} letterSpacing="0.08em" textTransform="uppercase">
            Preview
          </Text>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default TagExamPaperModal;
